import * as React from "react";

import { isAxiosError } from "axios";
import { isPast } from "date-fns";
import * as analytics from "@/services/analytics";
import { useLocation } from "react-router";
import { isNotLoginRequired } from "@/helpers/auth";
import * as Sentry from "@sentry/react";
import { useToggle } from "react-use";
import { SessionTimeoutWarningModal } from "@/components/modals/session-timeout-warning-modal";
import { apiInstance } from "@/api/base";

interface Props {
  children: React.ReactNode;
}

const VERIFY_TOKEN_EXPIRY = "__VERYFI_TOKEN__".toLowerCase() !== "false";

const SESSION_CHECK_INTERVAL = 3000;
const SESSION_EXPIRATION_THRESHOLD = 2 * 60 * 1_000;
const SESSION_EXPIRATION_RETRIES = 3;
const SESSION_EXPIRATION_RETRY_DELAY = 1_000;

const fetchSessionExpiration = async (): Promise<number> => {
  for (let attempt = 0; attempt <= SESSION_EXPIRATION_RETRIES; attempt++) {
    try {
      const { data } = await apiInstance.get<number>("/pf-auth/session-expiration-time");
      return data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 503 && attempt < SESSION_EXPIRATION_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, SESSION_EXPIRATION_RETRY_DELAY));
        continue;
      }
      throw error;
    }
  }
  throw new Error("Unreachable");
};

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const { pathname } = useLocation();
  const [showSessionTimeoutModal, setShowSessionTimeoutModal] = useToggle(false);
  const [nextCheckTime, setNextCheckTime] = React.useState<Date | null>(null);
  const showSessionTimeoutModalRef = React.useRef(showSessionTimeoutModal);

  React.useEffect(() => {
    showSessionTimeoutModalRef.current = showSessionTimeoutModal;
  }, [showSessionTimeoutModal]);

  const checkSessionStatus = React.useCallback(async () => {
    if (showSessionTimeoutModalRef.current) {
      return;
    }
    try {
      if (isNotLoginRequired(pathname)) {
        return;
      }

      try {
        const expirationTime = await fetchSessionExpiration();

        if (!VERIFY_TOKEN_EXPIRY) return;

        const expirationTimeInMs = expirationTime * 1_000;
        if (isPast(expirationTimeInMs)) {
          if (isNotLoginRequired(window.location.pathname)) return;
          analytics.sessionTimeout();
          document.location.href = "/pf-auth/logout?next_url=/session-expired";
          return;
        }
        if (!showSessionTimeoutModalRef.current && isAlmostExpired(new Date(expirationTimeInMs))) {
          setShowSessionTimeoutModal(true);
        }
        setNextCheckTime(new Date(expirationTimeInMs));
      } catch (_e) {
        if (isNotLoginRequired(window.location.pathname)) return;
        const state = btoa(pathname);
        const nextUrl = `/login?state=${state}`;
        document.location.href = `/pf-auth/logout?next_url=${encodeURIComponent(nextUrl)}`;
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [pathname, setShowSessionTimeoutModal, setNextCheckTime]);

  React.useEffect(() => {
    const calculatedDelay = nextCheckTime
      ? +nextCheckTime - Date.now() - SESSION_EXPIRATION_THRESHOLD
      : SESSION_CHECK_INTERVAL;
    const safeDelay = Math.max(calculatedDelay, 5 * 1000);
    const interval = setInterval(() => {
      void checkSessionStatus();
    }, safeDelay);
    if (nextCheckTime === null) {
      void checkSessionStatus();
    }

    return () => clearInterval(interval);
  }, [checkSessionStatus, nextCheckTime]);

  return (
    <>
      {children}
      {showSessionTimeoutModal && <SessionTimeoutWarningModal onRefresh={() => setShowSessionTimeoutModal()} />}
    </>
  );
};

const isAlmostExpired = (expirationDate: Date) => {
  const now = new Date();
  const diff = expirationDate.getTime() - now.getTime();
  return diff < SESSION_EXPIRATION_THRESHOLD;
};
